/* eslint-disable class-methods-use-this, max-len */
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { TitleCasePipe } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { untilDestroyed } from '@s2a-core/ng-core';
import { EquipmentQueryService, EquipmentService } from '@s2a-equipment/core';
import { EmbedAppMountedPath } from '@s2a/core';

import { environment } from '../../../environments/environment';
import { ComponentLifecycleManager } from '../../services/component.service';
import { PageLoaderService } from '../../services/pageLoader.service';
import { RouterService } from '../../services/router.service';

@Component({
  selector: 's2a-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [TitleCasePipe],
  standalone: false,
})
export class ShellComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('contentPlace') contentPlace: ElementRef;

  activatedAppUrl: string;
  isInitialized = false;
  private untilDestroyed = untilDestroyed();

  constructor(
    private componentLifecycleManager: ComponentLifecycleManager,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private routerService: RouterService,
    public pageLoaderService: PageLoaderService,
    private equipmentService: EquipmentService,
    private equipmentQueryService: EquipmentQueryService,
    private titleCasePipe: TitleCasePipe,
    private title: Title,
  ) {}

  ngOnInit(): void {
    this.equipmentService.ensureCache().pipe(this.untilDestroyed()).subscribe();
    combineLatest([
      this.activeRoute.data.pipe(
        map((routeData) => {
          this.activatedAppUrl = routeData.appUrl || '';
          if (this.activatedAppUrl.includes('errors')) {
            return 'errors';
          }

          return this.activatedAppUrl;
        }),
      ),
      this.equipmentQueryService
        .selectFilter('description', 'enterprise')
        .pipe(map((equipments) => (equipments.length ? equipments[0] : null))),
    ])
      .pipe(this.untilDestroyed())
      .subscribe(([service, equipment]) => {
        this.setTitle(service, equipment ? equipment.description : '');
      });

    this.routerService
      .getActiveRoute$()
      .pipe(this.untilDestroyed())
      .subscribe((event: MessageEvent) => {
        this.navigateShell(event);
      });
  }

  navigateShell(event: MessageEvent): void {
    if (event.data && 'value' in event.data) {
      const value = event.data.value;
      const currentPath = location.href.replace(`${location.origin}/`, '');
      const embedAppPath = value.startsWith(`${EmbedAppMountedPath}/`)
        ? value.replace(`${EmbedAppMountedPath}/`, '')
        : value;
      const urlSegment = this.getUrlSegment(embedAppPath as string);

      if (embedAppPath !== currentPath) {
        void this.router.navigate([`/${urlSegment.path}`], {
          queryParams: urlSegment.queryParams,
          replaceUrl: true,
        });
      }
    }
  }

  getUrlSegment(url: string): { path: string; queryParams?: any } {
    const queryStringStartIndex = url.indexOf('?');
    if (queryStringStartIndex > -1) {
      const queryParamsIterator = new URLSearchParams(url.slice(queryStringStartIndex + 1));
      const queryParams: any = {};
      queryParamsIterator.forEach((value, key) => {
        queryParams[key] = value;
      });

      return {
        path: url.slice(0, queryStringStartIndex),
        queryParams,
      };
    }

    return {
      path: url,
    };
  }

  setTitle(service: string, equipmentTitle: string): void {
    this.title.setTitle(
      `${equipmentTitle ? `${this.titleCasePipe.transform(equipmentTitle)} - ` : ''}${
        service ? `${this.titleCasePipe.transform(service)} - ` : ''
      } Share2Act`,
    );
  }

  ngAfterViewChecked(): void {
    if (!this.componentLifecycleManager.contentShell) {
      this.componentLifecycleManager.createComponent(this.contentPlace);
      this.renderApp();
    } else {
      if (!this.isInitialized) {
        this.renderApp();
      }
    }
  }

  renderApp(): void {
    this.pageLoaderService.render(environment.domainUrl.concat(`${EmbedAppMountedPath}${this.getUrl()}`));
    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.componentLifecycleManager.detachComponent();
    this.componentLifecycleManager.clear();
  }

  private getUrl(): string {
    const url = location.href;
    const urlWithDomain = url.replace(location.origin, '');
    if (!urlWithDomain.startsWith(`/${this.activatedAppUrl}`)) {
      return `/${this.activatedAppUrl}`;
    }

    return urlWithDomain;
  }
}
